/**
 * Standard validation error messages used across the application.
 * Centralizes error messages for consistency and easier maintenance.
 */
export const VALIDATION_MESSAGES = {
  // Required field messages
  required: (fieldName: string) => `${fieldName} is required`,

  // Email validation
  email: 'Please enter a valid email address',
  emailInvalid: 'Invalid email format',

  // Password validation
  passwordRequired: 'Password is required',
  passwordMinLength: 'Password must be at least 6 characters',
  passwordMismatch: 'Passwords do not match',
  passwordWeak: 'Password is too weak. Please use a stronger password',

  // Username validation
  usernameRequired: 'Username is required',
  usernameInvalid: 'Username contains invalid characters',

  // Common field messages
  firstNameRequired: 'First name is required',
  lastNameRequired: 'Last name is required',

  // Generic
  invalidFormat: (fieldName: string) => `Invalid ${fieldName} format`,
  minLength: (fieldName: string, min: number) => `${fieldName} must be at least ${min} characters`,
  maxLength: (fieldName: string, max: number) => `${fieldName} must not exceed ${max} characters`,
} as const;

/**
 * Standard success notification messages.
 */
export const SUCCESS_MESSAGES = {
  created: (entity: string) => `${entity} created successfully`,
  updated: (entity: string) => `${entity} updated successfully`,
  deleted: (entity: string) => `${entity} deleted successfully`,
  saved: (entity: string) => `${entity} saved successfully`,
  assigned: (entity: string) => `${entity} assigned successfully`,
  unassigned: (entity: string) => `${entity} unassigned successfully`,
  removed: (entity: string) => `${entity} removed successfully`,
  passwordChanged: 'Password changed successfully',
  statusChanged: (entity: string, status: string) => `${entity} ${status} successfully`,
  rolesUpdated: (entity: string) => `${entity} roles updated successfully`,
} as const;

/**
 * Standard error notification messages.
 */
export const ERROR_MESSAGES = {
  createFailed: (entity: string) => `Failed to create ${entity}`,
  updateFailed: (entity: string) => `Failed to update ${entity}`,
  deleteFailed: (entity: string) => `Failed to delete ${entity}`,
  loadFailed: (entity: string) => `Failed to load ${entity}`,
  saveFailed: (entity: string) => `Failed to save ${entity}`,
  assignFailed: (entity: string) => `Failed to assign ${entity}`,
  unassignFailed: (entity: string) => `Failed to unassign ${entity}`,
  passwordChangeFailed: 'Failed to change password',
  statusChangeFailed: (entity: string) => `Failed to change ${entity} status`,
  unexpectedError: 'An unexpected error occurred',
  networkError: 'Network error. Please check your connection',
  unauthorized: 'Unauthorized. Please check your credentials or login again.',
  unauthorizedAction: 'You are not authorized to perform this action',
  forbidden: 'You do not have permission to perform this action.',
  notFound: (entity: string) => `${entity} not found`,
  resourceNotFound: 'The requested resource was not found.',
  serverError: 'Server error. Please try again later.',
  validationError: 'Please check the form for errors',
  tenantRequired: 'Please select a tenant first',
  loginFailed: 'Login failed. Please check your credentials.',
  loginFailedAttempts: (attempts: number) => `Login failed. ${attempts} attempt(s) remaining.`,
  loginFailedWithMessage: (message: string, attempts: number) =>
    `${message} (${attempts} attempt(s) remaining)`,
  tooManyLoginAttempts: (minutes: number) =>
    `Too many login attempts. Please try again in ${minutes} minute(s).`,
} as const;

/**
 * Standard warning messages.
 */
export const WARNING_MESSAGES = {
  unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
  deleteConfirm: (entity: string) => `Are you sure you want to delete this ${entity}?`,
  actionIrreversible: 'This action cannot be undone',
} as const;
